import React, { useEffect, useState, useRef } from 'react';
import { bool, func, object, node } from 'prop-types';
import { isNil } from 'lodash';

const EmptyComponent = () => null;

/**
 * @deprecated Please use LazyComponent from @fiverr-private/ui_utils instead.
 */
const LazyComponent = ({
    lazyImport,
    shouldImport,
    fallback,
    placeholder,
    beforeLoad,
    afterLoad,
    onError,
    componentProps
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const componentRef = useRef();
    const isImported = !isNil(componentRef.current);
    const Component = isImported ? componentRef.current.default : null;

    useEffect(() => {
        const lazyLoadComponent = async() => {
            try {
                beforeLoad();
                componentRef.current = await lazyImport();
                afterLoad();
            } catch (error) {
                onError(error);
            }

            setIsLoading(false);
        };

        if (shouldImport && !isImported) {
            lazyLoadComponent();
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldImport, isImported]);

    if (isLoading) {
        return placeholder;
    }

    return isImported
        ? <Component {...componentProps}/>
        : fallback;
};

LazyComponent.propTypes = {
    /**
    * The lazy import function, with webpack's magic comments.
    */
    lazyImport: func.isRequired,
    /**
    * Controlled boolean from parent, that should be used for on-demend import.
    */
    shouldImport: bool,
    /**
    * The fallback component to be used when the lazy component fails to load.
    */
    fallback: node,
    /**
    * The placeholder component to be used when the lazy component is loading.
    */
    placeholder: node,
    /**
    * Function to be called before the component is loaded.
     */
    beforeLoad: func,
    /**
    * Function to be called after the component has loaded.
    */
    afterLoad: func,
    /**
    * Function to be called when the component fails to load.
    */
    onError: func,
    /**
    * Props to be passed to the component.
    */
    componentProps: object
};

LazyComponent.defaultProps = {
    shouldImport: true,
    fallback: <EmptyComponent/>,
    placeholder: <EmptyComponent/>,
    beforeLoad: Function.prototype,
    afterLoad: Function.prototype,
    onError: Function.prototype,
    componentProps: {}
};

export default LazyComponent;
